<template>
    <user-profile-dialog 
        :show="showSettingsDialog" 
        :email-val="profileData.email" 
        :phone-val="profileData.phone"
        :verification-method-val="profileData.verificationMethod" 
        @navigate-to-reset-password="navigateToResetPassword"
        @close="closeSettingsDialog"
    ></user-profile-dialog>
    <navigation-bar v-if="!isAccountPagesVisited" @open-settings-dialog="openSettingsDialog"></navigation-bar>
    <main>
        <side-menu v-if="!isAccountPagesVisited"></side-menu>
        <router-view></router-view>
        <notification-base v-if="!isAccountPagesVisited" 
            :show="showNotification" 
            :header-text="notificationHeader" 
            @dismiss-notification="dismissNotification"
        >
            <span>{{ notificationBody }}</span>
        </notification-base>
    </main>
</template>

<script>
import { computed } from "vue";
import utils from "./utils/utils";
import { loadLocale } from "./configs/localization/localeLoader";
import SideMenu from "./components/layout/SideMenu.vue";
import NavigationBar from "./components/layout/NavigationBar.vue";
import UserProfileDialog from "./components/layout/dialogs/UserProfileDialog.vue";

let repeater;

export default {
    components: {
        SideMenu,
        NavigationBar,
        UserProfileDialog
    },
    data() {
        return {
            notificationBody: "",
            timeoutCountdown: "",
            notificationHeader: "",
            showNotification: false,
            showSettingsDialog: false,
            formWarningPanelTimer: null,
            timeoutCountdownStartSeconds: 0,
            profileData: { email: null, phone: null, verificationMethod: null }
        }
    },
    computed: {
        isAccountPagesVisited() {
            return utils.accountRelatedPaths.includes(this.$route.path);
        },
        isAutoLoggedOut() {
            return this.$store.getters.isLoggedOut;
        },
        userLanguage() {
            return this.$store.getters.languageId;
        },
        showWarningPanel() {
            return this.$store.getters.hasError;
        },
        showFormWarningPanel() {
            return this.$store.getters.formHasError;
        },
        warningPanelText() {
            return this.$store.getters.warningPanelText;
        },
        isExplainableError() {
            return this.$store.getters.isExplainableError;
        },
        explainInProgress() {
            return this.$store.getters.explainInProgress;
        },
        pageIsLoading() {
            return this.$store.getters.pageIsLoading;
        },
        isSessionAboutToEnd() {
            return this.$store.getters.fireSessionNotification;
        },
        countdownValue() {
            return this.timeoutCountdown;
        },
        operationStatusIdle() {
            return this.$store.getters.operationStatusIdle;
        }
    },
    methods: {
        isGrantedPermission(permissionName) {
            return utils.utility.isGrantedPermission(permissionName);
        },
        isAllGrantedPermission(permissionNameList) {
            return utils.utility.isAllGrantedPermission(permissionNameList);
        },
        isAnyGrantedPermission(permissionNameList) {
            return utils.utility.isAnyGrantedPermission(permissionNameList);
        },
        isAvailableSource(sourceName) {
            return utils.utility.isAvailableSource(sourceName);
        },
        closeSettingsDialog() {
            this.showSettingsDialog = false;
        },
        async openSettingsDialog() {
            const userContactInfo = await this.$store.dispatch("fetchUserContactInfo");
            
            if (userContactInfo) {
                this.profileData.email = userContactInfo.email || "";
                this.profileData.phone = userContactInfo.phone || "";
                this.profileData.verificationMethod = userContactInfo.verificationSelect ? "email" : "gsm";
            }

            this.showSettingsDialog = true;
        },
        async navigateToResetPassword() {
            this.closeSettingsDialog();

            await this.$store.dispatch("putPasswordPermission");
            //this.$store.dispatch("logout");

            this.$router.replace("/reset-password");
        },
        dismissNotification() {
            this.showNotification = false;

            const isUserIdle = this.$store.getters.operationStatusIdle;

            if (isUserIdle) this.$store.dispatch("putUserIdleStatus", false);
        },
        startFormWarningPanelTimer() {
            this.clearFormWarningPanelTimer();

            const duration = this.isExplainableError ? 15000 : 6000;

            this.formWarningPanelTimer = setTimeout(() => {
                this.$store.dispatch("clearFormError");
            }, duration);
        },
        clearFormWarningPanelTimer() {
            if (!this.formWarningPanelTimer) return;

            clearTimeout(this.formWarningPanelTimer);

            this.formWarningPanelTimer = null;
        },
        startSignOffCountdown() {
            this.timeoutCountdownStartSeconds = this.$store.getters.remainingSessionTime;

            repeater = setInterval(() => {
                let min = Math.floor(this.timeoutCountdownStartSeconds / 60);
                let sec = this.timeoutCountdownStartSeconds % 60;

                if (min <= 0 && sec <= -1) return;

                sec = sec < 10 ? "0" + sec : sec;

                this.timeoutCountdown = `0${min}:${sec}`;

                this.timeoutCountdownStartSeconds--;
            }, 1000);
        }
    },
    watch: {
        isAutoLoggedOut(newVal, oldVal) {
            if (newVal && newVal != oldVal) this.$router.replace("/login");
        },
        async userLanguage(newVal, oldVal) {
            if (newVal && newVal != oldVal) {
                await loadLocale(newVal);

                this.$i18n.locale = newVal;
            }
        },
        showWarningPanel(newVal) {
            if (newVal) {
                setTimeout(() => {
                    this.$store.dispatch("clearError");
                }, 6000);
            }
        },
        showFormWarningPanel(newVal) {
            if (newVal) {
                this.startFormWarningPanelTimer();
            } else {
                this.clearFormWarningPanelTimer();
            }
        },
        explainInProgress(newVal) {
            if (newVal) {
                this.clearFormWarningPanelTimer();
            } else if (this.showFormWarningPanel) {
                this.startFormWarningPanelTimer();
            }
        },
        isSessionAboutToEnd(newVal, oldVal) {
            if (newVal != oldVal) {
                this.notificationHeader = this.$t("general.signoffWarning");
                this.notificationBody = `${this.$t("general.signoffDescription")} (${this.countdownValue})`;

                this.showNotification = newVal;

                if (newVal) this.startSignOffCountdown();
            }
        },
        operationStatusIdle(newVal) {
            if (newVal) {
                this.notificationHeader = this.$t("general.idleUserWarning");
                this.notificationBody = `${this.$t("general.idleUserDescription")}`;

                this.showNotification = newVal;
            } else if (!this.isSessionAboutToEnd) {
                this.showNotification = false;
            }
        }
    },
    provide() {
        return {
            isAvailableSource: this.isAvailableSource,
            isGrantedPermission: this.isGrantedPermission,
            pageIsLoading: computed(() => this.pageIsLoading),
            isAllGrantedPermission: this.isAllGrantedPermission,
            isAnyGrantedPermission: this.isAnyGrantedPermission,
            showWarningPanel: computed(() => this.showWarningPanel),
            warningPanelText: computed(() => this.warningPanelText),
            isExplainableError: computed(() => this.isExplainableError),
            showFormWarningPanel: computed(() => this.showFormWarningPanel)
        }
    },
    async created() {
        await this.$store.dispatch("tryLogin");
        
        if (this.$store.getters.username) {
            const isInternalUser = utils.utility.isInternalUser(this.$store.getters.username);

            if (!isInternalUser) {
                await this.$store.dispatch("fetchPasswordProcessStatus")

                const passwordChangeProcessOngoing = this.$store.getters.isPasswordResetProcess;

                if (passwordChangeProcessOngoing) {
                    this.$store.dispatch("logout");

                    this.$router.replace("/login");
                }
            }
        }
    },
    beforeUnmount() {
        clearInterval(repeater);
    }
}
</script>

<style>

* {
    box-sizing: border-box;
}

html {
    font-family: "Helvetica Neue", "Segoe UI", "Roboto", Arial, sans-serif;
}

body {
    margin: 0;
}

.wide {
    width: calc(100% - var(--sniffer-page-gutter)) !important;
}

:root {
    --sniffer-page-gutter: 2rem;
    --sniffer-font-size-14: 14px;
    --sniffer-font-size-15: 15px;
    --sniffer-empty-color: #6c757d;
    --sniffer-color-black: #000000;
    --sniffer-font-weight-medium: 600;
    --sniffer-color-primary: #0D4BA0;
    --sniffer-color-warning: #fceccc;
    --sniffer-font-size-sub-menu: 12.5px;
    --sniffer-color-secondary: #FFC20C;
    --sniffer-color-dark-grey: #d9d9d9;
    --sniffer-color-icon-small: #5c5c5c;
    --sniffer-color-light-blue: #f6faff;
    --sniffer-color-light-grey: #f9f9f9;
    --sniffer-color-active-menu: #f6faff;
    --sniffer-color-medium-grey: #fafafa;
    --sniffer-color-warning-text: #80391e;
    --sniffer-color-primary-light: #d9e8f9;
    --sniffer-color-primary-hover: #0b4088;
    --sniffer-color-warning-border: #fcdcb4;
    --sniffer-color-secondary-hover: #e9b106;
    --sniffer-color-primary-disabled: #618ac1;
    --sniffer-color-misspell-warning: #e36c34;
    --sniffer-color-danger: #c62828;
    --sniffer-color-danger-light: #fde7e7;
    --sniffer-color-success: #2e7d32;
    --sniffer-color-success-light: #e7f5e9;
    --sniffer-color-secondary-light: #fff6d6;
    --sniffer-color-text-muted: #6b6b6b;
    --sniffer-color-border: rgba(242, 242, 242, 255);
}

.backdrop {
    top: 0;
    left: 0;
    width: 100%;
    z-index: 150;
    height: 100vh;
    position: fixed;
    background-color: rgba(0, 0, 0, 0.3);
}

.dialog_backdrop {
    z-index: 50 !important;
}

.dialog_pop_up_backdrop {
    z-index: 110 !important;
}

.select2,
.select2-container {
    appearance: none !important;
    -moz-appearance: none !important;
    background-size: .6em !important;
    -webkit-appearance: none !important;
    background-repeat: no-repeat !important;
    background-position: calc(100% - 1.3em) center !important;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Ctitle%3Edown-arrow%3C%2Ftitle%3E%3Cg%20fill%3D%22%23000000%22%3E%3Cpath%20d%3D%22M10.293%2C3.293%2C6%2C7.586%2C1.707%2C3.293A1%2C1%2C0%2C0%2C0%2C.293%2C4.707l5%2C5a1%2C1%2C0%2C0%2C0%2C1.414%2C0l5-5a1%2C1%2C0%2C1%2C0-1.414-1.414Z%22%20fill%3D%22%23000000%22%3E%3C%2Fpath%3E%3C%2Fg%3E%3C%2Fsvg%3E") !important;
}

.select2-container--default .select2-selection--single {
    border-radius: 6px !important;
    border: .9px solid var(--sniffer-color-dark-grey) !important;
}

.select2-selection {
    height: 36px !important;
    padding: 3px !important;
}

.select2-selection__arrow {
    padding: 17px 15px !important;
}

.select2-container--default .select2-results__option--highlighted[aria-selected] {
    background-color: var(--sniffer-color-primary) !important;
}

.select2-results__option--highlighted[aria-selected]:has(> div.helper_buttons),
.select2-results__option[aria-selected=true]:has(> div.helper_buttons) {
    background-color: white !important;
}

input.select2-search__field {
    margin-top: 7px !important;
    font-size: 14px !important;
}

.v3ti--focus {
    outline: 0 !important;
    border-color: transparent !important;
    box-shadow: 0 0 3px 3px rgba(194, 210, 231, 250) !important;
}

.v3ti .v3ti-tag {
    background: var(--sniffer-color-primary) !important;
}

.v3ti .v3ti-tag .v3ti-remove-tag {
    color: white !important;
    transition: color .3s !important;
}

.v3ti .v3ti-tag .v3ti-remove-tag:hover {
    color: var(--sniffer-color-secondary) !important;
}

.form_select_wrapper.has-summary .select2-selection__choice {
    display: none !important;
}

.form_select_wrapper.has-summary .select2-selection__rendered::after {
    border: 0;
    margin: 0;
    padding: 0;
    font-size: 16px;
    font-weight: 400;
    color: #111827;
    font-family: inherit;
    display: inline-block;
    background: transparent;
    content: attr(data-summary);
}

.form_select_wrapper .select2-selection__clear {
    font-size: 25px;
    margin-right: 10px;
}

.simple-typeahead-input {
    width: 100%;
    display: block;
    line-height: 30px;
    padding: 2px 12px;
    border-radius: 3px;
    border: 1px solid var(--sniffer-color-dark-grey);
}

.simple-typeahead-input:focus {
    outline: 0;
    border-color: transparent;
    box-shadow: 0 0 3px 3px rgba(194, 210, 231, 250);
}

.simple-typeahead-list,
.simple-typeahead-list-item {
    width: 100%;
}
</style>